import { useEffect, useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Button, Stack, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

type RefreshButtonProps = {
  onRefresh: () => void | Promise<void>;
  lastUpdatedAt?: Date | null;
  disabled?: boolean;
  label?: string;          // button text, defaults to "Refresh"
  intervalMs?: number;     // ticker, defaults to 30s
  size?: "small" | "medium" | "large";
  variant?: "text" | "outlined" | "contained";
};

export default function RefreshButton({
  onRefresh,
  lastUpdatedAt,
  disabled,
  label = "Refresh",
  intervalMs = 30_000,
  variant = "outlined",
}: RefreshButtonProps) {
  // Internal ticker to recompute "Updated ..." without parent involvement
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdatedAt) return null;
    // `now` in deps to refresh text every tick
    void now;
    return formatDistanceToNow(lastUpdatedAt, { addSuffix: true, includeSeconds: false });
  }, [lastUpdatedAt, now]);

  return (
    <Stack alignItems="flex-end" spacing={0.25}>
      <Button
        onClick={onRefresh}
        variant={variant}
        startIcon={<RefreshIcon />}
        disabled={disabled}
      >
        {label}
      </Button>
      {lastUpdatedLabel && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
          Updated {lastUpdatedLabel}
        </Typography>
      )}
    </Stack>
  );
}
